import { useContext } from "react";
import styled from "styled-components";
import {
  Flex,
  Text,
  Box,
  ViewerContext,
  Avatar,
  DropDown,
  Icon,
  useScreenResolution,
  useCheckIfSubscriptionFeatureActiveBroker,
} from "dash-ui";
import { BrokerSubscriptionTypesEnum } from "dash-common";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { RoutesPath } from "flow-components/routes/routes";
import { ProfileTabsKeys } from "pages/ProfilePage/ProfilePage.constants";
import { useLogout } from "common/hooks/useLogout/useLogout";

const AnchorContainer = styled(Flex)`
  height: inherit;
`;
const AvatarContainer = styled(Box)<{ isOpen: boolean }>`
  border-radius: 50%;
  border: 1px solid
    ${({ isOpen, theme }) => (isOpen ? theme.colors.b50 : "transparent")};
`;

function ProfileDropdown() {
  const { broker, account, profile } = useContext(ViewerContext);
  const { t } = useTranslation("common", { keyPrefix: "labels" });
  const { t: tProfileLabels } = useTranslation("pages", {
    keyPrefix: "profilePage.labels",
  });
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const navigate = useNavigate();
  const { isSubscriptionActive } = useCheckIfSubscriptionFeatureActiveBroker();
  const [logout, loading] = useLogout();

  const onLogoutButtonClick = () => {
    logout();
  };

  const isDemoBroker =
    broker?.subscriptionType === BrokerSubscriptionTypesEnum.DEMO;

  const getProfileName = () => {
    // If SSO user didn't finish his account (so we don't have a broker created), we will simply display the email
    return profile
      ? `${profile?.lastName} ${
          profile?.firstName ? profile.firstName[0] : ""
        }.`
      : account?.email;
  };

  return account ? (
    <Box mr={isMobile ? 4 : 14} data-testid="profile">
      <DropDown
        placementHorizontal="left"
        Anchor={({ isOpen }: { isOpen: boolean }) => (
          <AnchorContainer alignItems="center" w="100%" py={2}>
            <Flex alignItems="center">
              <Icon size={24} name={isOpen ? "arrowUp" : "arrowDown"} />
              <Text>{getProfileName()}</Text>
              <AvatarContainer isOpen={isOpen} ml={2}>
                <Avatar size={32} url={profile?.avatarFile?.publicUrl || ""} />
              </AvatarContainer>
            </Flex>
          </AnchorContainer>
        )}
        items={[
          {
            mt: 1,
            key: "accountInfo",
            icon: "personOutline",
            children: tProfileLabels`accountInfo`,
            contentCentered: false,
            w: 180,
            iconProps: { size: 22 },
            onClick: () =>
              navigate(
                profile
                  ? `${RoutesPath.Profile}?tab=${ProfileTabsKeys.ACCOUNT_INFO}`
                  : RoutesPath.CompleteSignUpSSO
              ),
          },
          {
            w: "100%",
            key: "billing",
            color: "default",
            icon: "listAlt",
            children: tProfileLabels`billing`,
            iconProps: { size: 22 },
            contentCentered: false,
            disabled: isDemoBroker || !isSubscriptionActive,
            onClick: () =>
              navigate(`${RoutesPath.Profile}?tab=${ProfileTabsKeys.BILLING}`),
          },
          {
            w: "100%",
            key: "settings",
            icon: "settings",
            children: tProfileLabels`settings`,
            contentCentered: false,
            iconProps: { size: 22 },
            disabled: !isSubscriptionActive,
            onClick: () =>
              navigate(`${RoutesPath.Profile}?tab=${ProfileTabsKeys.SETTINGS}`),
          },
          {
            w: "100%",
            key: "feedback",
            icon: "note",
            children: tProfileLabels`feedback`,
            contentCentered: false,
            iconProps: { size: 22 },
            onClick: () =>
              window.open(
                "https://dashtodash.com/user-feedback-survey/",
                "_blank"
              ),
          },
          {
            w: "100%",
            key: "support",
            icon: "infoCircle",
            children: tProfileLabels`support`,
            contentCentered: false,
            iconProps: { size: 22 },
            onClick: () => window.open("https://help.dashtodash.com", "_blank"),
          },
          {
            w: "100%",
            mb: 1,
            key: "logout",
            contentCentered: false,
            icon: "logout",
            iconProps: { size: 22 },
            children: t`signOut`,
            onClick: onLogoutButtonClick,
            disabled: loading,
          },
        ]}
      />
    </Box>
  ) : null;
}

export default ProfileDropdown;
